import logger from './logger';

/**
 * @param array - array of strings
 * @param repeats - min count of repeats
 * @returns array of unique strings with repeats and sorted by repeats + alphabetically
 */
export const getUniqueStringsWithRepeats = (array: string[], repeats: number = 3): string[] => {
  const wordCounts = array.reduce<Record<string, { word: string; count: number }>>((counts, word) => {
    const key = word.toLowerCase();
    if (counts[key]) {
      // eslint-disable-next-line no-param-reassign
      counts[key].count += 1;
    } else {
      // eslint-disable-next-line no-param-reassign
      counts[key] = { word, count: 1 };
    }
    return counts;
  }, {});

  const uniqueWords = Object.values(wordCounts)
    .filter(({ count }) => count >= repeats)
    .sort((a, b) => {
      if (b.count === a.count) {
        return a.word.localeCompare(b.word);
      }
      return b.count - a.count;
    });

  logger.log(
    'CHIPS_LOGGING getUniqueStringsWithRepeats',
    uniqueWords.map(({ word, count }) => `${word} (${count})`),
  );

  return uniqueWords.map(({ word }) => word);
};

/**
 * @param firstArray - array of strings
 * @param secondArray - array of strings
 * @returns array of strings that exists (ignoring case) in both firstArray and secondArray
 */
export const getIntersection = (firstArray: string[], secondArray: string[]): string[] => {
  const secondArrayLowerCase = secondArray.map((str) => str.toLowerCase());
  const matchingStrings: string[] = [];

  for (let i = 0; i < firstArray.length; i += 1) {
    const currentString = firstArray[i];
    if (secondArrayLowerCase.includes(currentString.toLowerCase())) {
      matchingStrings.push(currentString);
    }
  }

  return matchingStrings;
};

/**
 * @param firstArray - array of strings
 * @param secondArray - array of strings
 * @returns array of strings that exists (ignoring case) in firstArray and not in secondArray
 */
export const arrayDifference = (firstArray: string[], secondArray: string[]): string[] => {
  const secondArrayLowerCase = secondArray.map((item) => item.toLowerCase());
  return firstArray.filter((item) => !secondArrayLowerCase.includes(item.toLowerCase()));
};

/**
 * @param array - array of strings
 * @param str - string to search for
 * @returns boolean, true if string exists in array (case insensitive)
 */
export const includesCaseInsensitive = (array: string[], str: string): boolean => {
  const lowerCaseString = str.toLowerCase();
  return array.some((item) => item.toLowerCase() === lowerCaseString);
};

/**
 * @param array - array of strings
 * @param str - string to find
 * @returns string | undefined - returns the string if found (case insensitive) or undefined
 */
export const findCaseInsensitive = (array: string[], str: string): string | undefined => {
  const lowerCaseString = str.toLowerCase();
  return array.find((item) => item.toLowerCase() === lowerCaseString);
};
